import { isFedrampModerate } from '@atlaskit/atlassian-context/is-fedramp-moderate';
import type { Perimeter } from '@atlaskit/atlassian-context/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { UFOExperience } from '@atlassian/ufo-experimental/experience';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
} from '@atlassian/ufo-experimental/experience-types';

import type {
	CategorizedStorageControlsCache,
	CategoryStorageType,
	StorageControlsPatternFormat,
} from '../../../types';
import {
	__internalSetCacheLoadedDate,
	__internalSetCdnFileVersion,
} from '../bsc-global-state/core';
import { getFetchPatternFormat } from '../feature-flagging';
import { Logger } from '../logger';
import {
	getCategoryCacheUrlPerimeterLookup,
	getCommercialCategoryCacheUrlLookup,
} from './constants';

export class CacheLoader {
	private cacheUrl: string;

	/**
	 * The format of the file `cacheUrl` points at — the layout this process decided to fetch. Read from
	 * that single decision rather than passed in, so the URL and the format recorded on the fetched
	 * payload cannot come from different gate evaluations.
	 *
	 * TEMPORARY: Will be removed when the related feature flags are cleaned up (`platform_bsc_ssr_flat_pattern_file_format`,
	 * `platform_bsc_flat_pattern_file_structure`)
	 */
	private readonly fetchedPatternFormat: StorageControlsPatternFormat;

	/**
	 * Cache variable for storing categorized cookies cache
	 */
	private localResponseCache: CategorizedStorageControlsCache | undefined;

	/**
	 * Cache variable for storing a promise that's been fired by a different invocation of loadStorageControls
	 *
	 * Allows for many invocations to happen simultaneously, and for only one fetch to go out
	 */
	private localResponsePromiseCache: Promise<Response> | undefined;

	/**
	 * Cache variable for storing the promise returned from the response object's json method
	 *
	 * Ensures we don't invoke response.json multiple times
	 */
	private localResponseJsonPromiseCache:
		| Promise<CategorizedStorageControlsCache | undefined>
		| undefined;

	constructor(storageType: CategoryStorageType, perimeter?: Perimeter) {
		// Asked for, not passed in: whoever constructs a loader gets the layout this process already
		// decided to fetch, so no caller can hand it a format that disagrees with everyone else's.
		this.fetchedPatternFormat = getFetchPatternFormat();

		// Auto-detect perimeter from the Atl-Ctx-Perimeter cookie when not explicitly provided.
		// isFedrampModerate() reads the cookie set by the platform; if absent, the perimeter is commercial.
		// Gated by platform_bsc_auto_detect_perimeter for safe rollout.
		// Wrapped in try/catch to ensure a cookie-read failure never breaks BSC initialisation.
		const detectPerimeter = (): Perimeter => {
			try {
				return isFedrampModerate() ? 'fedramp-moderate' : 'commercial';
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'detectPerimeterError',
					message: e?.message || 'Failed to detect perimeter via isFedrampModerate',
				});
				return 'commercial';
			}
		};

		const resolvedPerimeter: Perimeter =
			perimeter ?? (fg('platform_bsc_auto_detect_perimeter') ? detectPerimeter() : 'commercial');

		const useFlatFormat = this.fetchedPatternFormat === 'flat';
		const cacheUrlLookup =
			getCategoryCacheUrlPerimeterLookup(useFlatFormat)[resolvedPerimeter] ||
			getCommercialCategoryCacheUrlLookup(useFlatFormat);
		this.cacheUrl = cacheUrlLookup[storageType];
	}

	clearStorageControlsDataCache(): void {
		this.localResponseCache = undefined;
		this.localResponsePromiseCache = undefined;
		this.localResponseJsonPromiseCache = undefined;
	}

	/**
	 * Unpacks the response promise from the categories CDN
	 *
	 * errors if that request was unsuccessful, returns the categories data otherwise
	 *
	 */
	private async handleCategoriesResponse(responsePromise: Promise<Response> | undefined) {
		if (!responsePromise) {
			throw new Error(`Failed to fetch Cookies Cache! Cached promise was undefined`);
		}

		const response = await responsePromise;
		if (!response.ok) {
			throw new Error(`Failed to fetch Cookies Cache! status: ${response.status}`);
		}
		// Checks if we've already fired the response.json() promise
		this.localResponseJsonPromiseCache = this.localResponseJsonPromiseCache || response?.json();

		return this.localResponseJsonPromiseCache;
	}

	getStorageControlsDataFromCache(): CategorizedStorageControlsCache | undefined {
		return this.localResponseCache;
	}

	/**
	 * The format of the file this loader fetches.

	* TEMPORARY: Will be removed when the related feature flags are cleaned up (`platform_bsc_ssr_flat_pattern_file_format`,
	 * `platform_bsc_flat_pattern_file_structure`)
	 */
	getFetchedPatternFormat(): StorageControlsPatternFormat {
		return this.fetchedPatternFormat;
	}

	async loadStorageControlsData(): Promise<CategorizedStorageControlsCache | undefined> {
		const isFetchTrackingSampled = !Math.floor(Math.random() * 100);
		let fetchExperience: UFOExperience | null = null;

		if (isFetchTrackingSampled) {
			fetchExperience = new UFOExperience('browser-storage-controls-fetch-time', {
				type: ExperienceTypes.Load,
				performanceType: ExperiencePerformanceTypes.Custom,
				platform: { component: 'browser-storage-controls' },
			});
			fetchExperience.start();
		}

		if (this.localResponseCache) {
			if (fetchExperience) {
				fetchExperience.success();
			}
			return this.localResponseCache;
		} else {
			// Always use Prod CDN for prod/local/dev/staging environments, but stub the route for automation tests
			try {
				if (!this.localResponsePromiseCache) {
					Logger.debug(`Loading cache ${this.cacheUrl}`);
					// invoke CDN and cache the promise
					this.localResponsePromiseCache = fetch(this.cacheUrl);
				}

				// unpack response
				this.localResponseCache = await this.handleCategoriesResponse(
					this.localResponsePromiseCache,
				);

				// Record what we actually fetched, so pattern matching never has to re-evaluate the
				// rollout gate to work out how to read it. clearStorageControlsDataCache() drops the
				// payload, so a re-fetch re-records from that loader's own format. A malformed response
				// can parse to a primitive, which cannot carry the field — those fall back to the legacy
				// trie reading, and match nothing either way.
				if (typeof this.localResponseCache === 'object' && this.localResponseCache !== null) {
					this.localResponseCache.patternFormat = this.fetchedPatternFormat;
				}

				__internalSetCacheLoadedDate(new Date().toISOString().slice(0, 10));

				// Extract and store CDN file version (write-once: first loader to resolve wins)
				if (
					typeof this.localResponseCache === 'object' &&
					this.localResponseCache !== null &&
					this.localResponseCache.version
				) {
					__internalSetCdnFileVersion(this.localResponseCache.version);
				}

				if (fetchExperience) {
					fetchExperience.success();
				}

				return this.localResponseCache;
			} catch (e: any) {
				if (fetchExperience) {
					fetchExperience.failure({ metadata: { error: String(e) } });
				}

				Logger.errorWithOperationalEvent({
					action: 'loadStorageControlsError',
					message: e.message || '',
				});

				this.clearStorageControlsDataCache();
			}
		}
	}
}

export {
	getCategoryCacheUrlPerimeterLookup,
	getCommercialCategoryCacheUrlLookup,
} from './constants';
